/**
 * Top Bar Navigation
 * 
 * Classic horizontal navigation bar at the top of the page.
 * This is the default navigation style.
 */

import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Calendar, Users, Heart } from "lucide-react";
import { useNavigationItems } from "@/hooks/useNavigationItems";
import { useSiteConfig } from "@/lib/useSiteConfig";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useLayout } from "@/lib/layout-provider";
import { cn } from "@/lib/utils";

const NavigationTopBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { currentLayout } = useLayout();
  const { features, navigation } = useSiteSettings();
  const config = useSiteConfig();
  const { navigationItems: navItems, loading: navLoading } = useNavigationItems();

  const isActive = (path: string) => location.pathname === path;
  const navConfig = currentLayout.navigation;

  return (
    <nav 
      className={cn(
        "bg-background/95 backdrop-blur-sm border-b border-border z-50 shadow-warm",
        navConfig.sticky && "sticky top-0",
        navConfig.transparent && "bg-transparent border-transparent shadow-none"
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo / Site Name */}
          <Link to="/" className="flex items-center space-x-2">
            {config.logoUrl && config.logoUrl.trim() !== '' && (
              <img
                src={config.logoUrl}
                alt={config.organizationName}
                className="h-10 w-auto object-contain"
                onError={(e) => {
                  console.error('[NavigationTopBar] Failed to load logo:', config.logoUrl);
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <span className="text-xl font-bold text-foreground">
              {config.organizationName}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={cn(
                  "px-3 py-2 text-sm font-medium transition-colors",
                  isActive(item.path)
                    ? "text-heritage border-b-2 border-heritage"
                    : "text-foreground hover:text-heritage"
                )}
              >
                {item.name}
              </Link>
            ))}
            
            {/* CTA Buttons */}
            <div className="flex items-center space-x-3">
              {features.events.enabled && navigation.showEventsButton && (
                <Button variant="culture" size="sm" asChild>
                  <Link to="/events">
                    <Calendar className="w-4 h-4" />
                    {features.events.buttonText}
                  </Link>
                </Button>
              )}
              {features.membership.enabled && (
                <Button variant="heritage" size="sm" asChild>
                  <Link to="/membership">
                    <Users className="w-4 h-4" />
                    {features.membership.buttonText}
                  </Link>
                </Button>
              )}
              {features.donation.enabled && (
                <Button variant="donate" size="sm" asChild>
                  <Link to="/donate">
                    <Heart className="w-4 h-4" />
                    {features.donation.buttonText}
                  </Link>
                </Button>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-foreground hover:text-heritage"
              aria-label="Toggle menu"
              tabIndex={0}
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-background border-t border-border">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    "block px-3 py-2 text-base font-medium transition-colors",
                    isActive(item.path)
                      ? "text-heritage bg-secondary"
                      : "text-foreground hover:text-heritage hover:bg-secondary"
                  )}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile CTA Buttons */}
              <div className="px-3 py-4 space-y-2">
                {features.events.enabled && navigation.showEventsButton && (
                  <Button variant="culture" className="w-full" asChild>
                    <Link to="/events" onClick={() => setIsOpen(false)}>
                      <Calendar className="w-4 h-4" />
                      {features.events.buttonText}
                    </Link>
                  </Button>
                )}
                {features.membership.enabled && (
                  <Button variant="heritage" className="w-full" asChild>
                    <Link to="/membership" onClick={() => setIsOpen(false)}>
                      <Users className="w-4 h-4" />
                      {features.membership.pageTitle}
                    </Link>
                  </Button>
                )}
                {features.donation.enabled && (
                  <Button variant="donate" className="w-full" asChild>
                    <Link to="/donate" onClick={() => setIsOpen(false)}>
                      <Heart className="w-4 h-4" />
                      {features.donation.pageTitle}
                    </Link>
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default NavigationTopBar;




